import React from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';

import { Box, Stack, Button, Slider, Typography, CircularProgress } from '@material-ui/core';
import { PiFlashlight } from 'react-icons/pi';
import { useIsRendered } from '../hooks/useIsRendered';
import { useLanguage } from '../contexts/language';
import String from './String';

const SCANNER_CONTAINER_ID = 'qr-scanner-container';

const SCANNER_QRBOX_REDUCER = 0.9; // %
const SCANNER_QRBOX_MIN = 50; // px

export const QrScanner = ({ fps = 1, onScan, onError, showLoader }) => {
  const [supportsTorch, setSupportsTorch] = React.useState(false);
  const [torchEnabled, setTorchEnabled] = React.useState(false);

  const [supportsZoom, setSupportsZoom] = React.useState(false);
  const [zoomStatus, setZoomStatus] = React.useState({
    step: 0.1,
    min: 1,
    max: 8,
    value: null,
  });

  const [scannerSettings, setScannerSettings] = React.useState(null);

  const isRendered = useIsRendered();
  const { content } = useLanguage();

  const scanner = React.useMemo(() => {
    if (!isRendered) return null;
    return new Html5Qrcode(SCANNER_CONTAINER_ID);
  }, [isRendered]);

  const toggleTorch = React.useCallback(() => {
    if (!scanner || !supportsTorch) return;
    const state = scanner.getState();
    if (state !== Html5QrcodeScannerState.SCANNING && state !== Html5QrcodeScannerState.PAUSED) {
      return;
    }
    const capabilities = scanner.getRunningTrackCameraCapabilities();
    const torchFea = capabilities.torchFeature();
    const newVal = !torchFea.value();
    setTorchEnabled(newVal);
    torchFea.apply(newVal);
  }, [scanner, supportsTorch]);

  const updateZoomValue = React.useCallback(
    (value) => {
      if (!scanner || !supportsZoom) return;
      const state = scanner.getState();
      if (state !== Html5QrcodeScannerState.SCANNING && state !== Html5QrcodeScannerState.PAUSED) {
        return;
      }
      const capabilities = scanner.getRunningTrackCameraCapabilities();
      const zoomFeat = capabilities.zoomFeature();
      setZoomStatus((prev) => ({ ...prev, value }));
      zoomFeat.apply(value);
    },
    [scanner, supportsZoom]
  );

  React.useEffect(() => {
    if (!scanner) return;

    const config = {
      fps,
      qrbox: (viewWidth, viewHeight) => {
        const width = Math.max(viewWidth * SCANNER_QRBOX_REDUCER, SCANNER_QRBOX_MIN);
        const height = Math.max(viewHeight * SCANNER_QRBOX_REDUCER, SCANNER_QRBOX_MIN);
        return { width, height };
      },
    };

    const stopScanner = async () => {
      try {
        const state = scanner.getState();
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          await scanner.stop();
        }
      } catch (error) {
        console.error(error);
      }
    };

    const startScanner = async () => {
      try {
        if (scanner.getState() === Html5QrcodeScannerState.NOT_STARTED) {
          await scanner.start({ facingMode: 'environment' }, config, onScan, onError);
        }

        const settings = scanner.getRunningTrackSettings();
        setScannerSettings(settings);

        const capabilities = scanner.getRunningTrackCameraCapabilities();

        const suppTorch = capabilities.torchFeature().isSupported();
        setSupportsTorch(suppTorch);
        if (suppTorch) {
          setTorchEnabled(!!capabilities.torchFeature().value());
        }

        const suppZoom = capabilities.zoomFeature().isSupported();
        setSupportsZoom(suppZoom);

        if (suppZoom) {
          setZoomStatus({
            step: capabilities.zoomFeature().step(),
            min: capabilities.zoomFeature().min(),
            max: capabilities.zoomFeature().max(),
            value: capabilities.zoomFeature().value(),
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    startScanner();
    return () => {
      stopScanner();
    };
  }, [scanner, fps, onScan, onError]);

  const scannerContainerRef = React.useRef(null);
  React.useEffect(() => {
    // Delay can help Android
    const id = setTimeout(() => {
      scannerContainerRef?.current?.focus();
    }, 500);
    return () => clearTimeout(id);
  }, []);

  return (
    <Stack direction="column" justifyContent="center" alignItems="center" height="100%" gap={4} padding={2}>
      <Box width="100%" textAlign="center">
        <Typography variant="caption">
          <String s="qr_frame_instruction" />
        </Typography>
      </Box>
      <Box
        ref={scannerContainerRef}
        id={SCANNER_CONTAINER_ID}
        tabIndex={-1}
        role="region"
        aria-label={content('aria_qr_scanner_preview')}
        width="100%"
        height="300px"
        borderRadius="15px"
        overflow="hidden"
        display="flex"
        justifyContent="center"
        alignItems="center"
        border="1px solid #ccc"
      />

      {showLoader && (
        <Box
          position="absolute"
          width="100%"
          height={scannerSettings?.height || '300px'}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={50} color="success" />
        </Box>
      )}

      <Stack direction="row" width="100%" alignItems="center" justifyContent="center" gap={4}>
        {supportsZoom && (
          <Box width="100%" px={1} textAlign="center">
            <Typography variant="subtitle1" fontWeight={700}>
              Zoom
            </Typography>
            <Slider
              aria-label="qr-scanner-zoom-slider"
              step={zoomStatus.step}
              min={zoomStatus.min}
              max={zoomStatus.max}
              value={zoomStatus.value ?? zoomStatus.min}
              onChange={(_, value) => updateZoomValue(value)}
              size="medium"
              color="primary"
            />
          </Box>
        )}
        {supportsTorch && (
          <Button
            variant="contained"
            color={torchEnabled ? 'warning' : 'inherit'}
            size="large"
            onClick={toggleTorch}
            startIcon={<PiFlashlight />}
          >
            Torch
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
